import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Send, Info, AlertTriangle, Zap } from 'lucide-react';
import type { MessageClassification } from './types';

interface MessageComposerProps {
  onSend: (data: {
    content: string;
    classification: MessageClassification;
    urgentReason?: string;
  }) => Promise<void>;
  placeholder?: string;
  showSubject?: boolean;
  onSubjectChange?: (subject: string) => void;
  subject?: string;
  disabled?: boolean;
  compact?: boolean;
}

const CLASSIFICATION_OPTIONS: {
  value: MessageClassification;
  label: string;
  description: string;
  icon: typeof Info;
  activeClass: string;
  inactiveClass: string;
}[] = [
  {
    value: 'informational',
    label: 'Informational',
    description: 'General update or note',
    icon: Info,
    activeClass: 'bg-slate-100 border-slate-400 text-slate-800',
    inactiveClass: 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50',
  },
  {
    value: 'action_required',
    label: 'Action Required',
    description: 'Needs a response or decision',
    icon: AlertTriangle,
    activeClass: 'bg-amber-50 border-amber-400 text-amber-800',
    inactiveClass: 'bg-white border-slate-200 text-slate-500 hover:bg-amber-50/50',
  },
  {
    value: 'urgent',
    label: 'Urgent',
    description: 'Time-sensitive, requires immediate attention',
    icon: Zap,
    activeClass: 'bg-rose-50 border-rose-400 text-rose-800',
    inactiveClass: 'bg-white border-slate-200 text-slate-500 hover:bg-rose-50/50',
  },
];

export function MessageComposer({
  onSend,
  placeholder = 'Type your message...',
  showSubject,
  onSubjectChange,
  subject,
  disabled,
  compact,
}: MessageComposerProps) {
  const [content, setContent] = useState('');
  const [classification, setClassification] = useState<MessageClassification>('informational');
  const [urgentReason, setUrgentReason] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!content.trim()) return;
    if (classification === 'urgent' && !urgentReason.trim()) return;

    setSending(true);
    try {
      await onSend({
        content: content.trim(),
        classification,
        urgentReason: classification === 'urgent' ? urgentReason.trim() : undefined,
      });
      setContent('');
      setUrgentReason('');
      setClassification('informational');
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`border-t bg-white ${compact ? 'p-3' : 'p-4'}`}>
      {/* Classification Selector */}
      <div className={`flex gap-2 ${compact ? 'mb-2' : 'mb-3'}`}>
        {CLASSIFICATION_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isActive = classification === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setClassification(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                isActive ? opt.activeClass : opt.inactiveClass
              }`}
              title={opt.description}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Urgent Reason (shown only when Urgent is selected) */}
      {classification === 'urgent' && (
        <div className="mb-3">
          <Label className="text-xs text-rose-600 font-medium mb-1 block">
            Reason for urgency (required, max 120 characters)
          </Label>
          <Input
            value={urgentReason}
            onChange={(e) => setUrgentReason(e.target.value.substring(0, 120))}
            placeholder="Brief reason this is urgent..."
            className="text-sm border-rose-200 focus:border-rose-400"
            maxLength={120}
          />
          <span className="text-[10px] text-muted-foreground mt-0.5 block text-right">
            {urgentReason.length}/120
          </span>
        </div>
      )}

      {/* Subject (optional, for new threads) */}
      {showSubject && (
        <div className="mb-3">
          <Input
            value={subject || ''}
            onChange={(e) => onSubjectChange?.(e.target.value)}
            placeholder="Thread subject..."
            className="text-sm"
          />
        </div>
      )}

      {/* Message Input */}
      <div className="flex gap-2 items-end">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`flex-1 resize-none text-sm ${compact ? 'min-h-[60px]' : 'min-h-[80px]'}`}
          disabled={disabled || sending}
        />
        <Button
          onClick={handleSend}
          disabled={
            !content.trim() ||
            (classification === 'urgent' && !urgentReason.trim()) ||
            sending ||
            disabled
          }
          size="icon"
          className="h-10 w-10 shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-[10px] text-muted-foreground mt-1.5">
        Press Cmd+Enter to send
      </p>
    </div>
  );
}
